import { FunctionALPackageSize, ProductInfoForKey, ProductKey } from "libs/product/src/lib/models/product.model";
import { FunctionALPackage, FunctionALPackageType, FunctionALTreatmentPackages, QuotationModel } from "../models";
import { FUNCTIONAL_REFINEMENT_LARGE_PACKAGE_PRICE, FUNCTIONAL_REFINEMENT_SMALL_PACKAGE_PRICE } from "libs/sap/src/lib/services/sap.fixed-prices.constants";

export class FunctionALPackagesService {

	/**
	 * Checks whether the treatment is running depending on the state of the packages
	 * @param packages
	 * @returns true, if packages have already been ordered
	 */
	public static isTreatmentRunning( packages: FunctionALTreatmentPackages ) : boolean {
		if(!packages){
			return false;
		}
		return !!packages.active || packages.closed.length > 0;
	}

	/**
	 * Moves a package from the 'pending' array to the 'active' "state"
	 * This is only possible if pending packages exist and if there is not
	 * already an active package
	 * @param packages
	 * @returns the updated packages structure
	 */
	public static advanceNextPackage( packages: FunctionALTreatmentPackages ) : FunctionALTreatmentPackages {
		if (!packages){
			return null;
		}
		if( packages.pending.length === 0){
			return packages;
		}
		if( packages.active ){
			return packages;
		}
		const nextPackage = packages.pending.shift();

		return {
			initialPlannedSteps: packages.initialPlannedSteps,
			pending: packages.pending,
			active: nextPackage,
			closed: packages.closed,
		}

	}

	/**
	 * Moves an 'active' package to the 'closed' array
	 * This is only possible if an active packages exists
	 * @param packages
	 * @returns the updated packages structure
	 */
	public static closeActivePackage( packages: FunctionALTreatmentPackages ) : FunctionALTreatmentPackages {
		if (!packages){
			return null;
		}
		if( !packages.active){
			return packages;
		}
		return {
			initialPlannedSteps: packages.initialPlannedSteps,
			pending: packages.pending,
			active: null,
			closed: [...packages.closed, packages.active]
		}
	}

	/**
	 * Calculates the number and size of packages based on the total steps
	 * @param totalSteps
	 * @returns the calculated array
	 */
	public static generatePendingPackageStructure(totalSteps: number): FunctionALPackage[] {
		if(totalSteps < 1) {
			return [];
		}

		const result: FunctionALPackage[] = [];
		const productKey = this.getProductKeyForAmountOfSteps(totalSteps);

		while (totalSteps > 0) {
			const steps = Math.min(FunctionALPackageSize.BASE_PACKAGE_SIZE, totalSteps);
			result.push({steps: steps, type: FunctionALPackageType.PLANNED});
			totalSteps -= steps;
		}

		if([ProductKey.FUNCTIONAL_SCHIENE_NEO, ProductKey.FUNCTIONAL_SCHIENE_PLUS].includes(productKey)) {
			result.push({steps: ProductInfoForKey[productKey].maxRefinementStepsPerPackage, type: FunctionALPackageType.REFINEMENT});
		}

		return result;
	}

	public static getProductKeyForAmountOfSteps( totalSteps: number) : ProductKey {
		const neoMaxSteps = FunctionALPackageSize.BASE_PACKAGE_SIZE * ProductInfoForKey[ProductKey.FUNCTIONAL_SCHIENE_NEO].maxBasePackages;
		const plusMaxSteps = FunctionALPackageSize.BASE_PACKAGE_SIZE * ProductInfoForKey[ProductKey.FUNCTIONAL_SCHIENE_PLUS].maxBasePackages;
		if (totalSteps <= neoMaxSteps) {
			return ProductKey.FUNCTIONAL_SCHIENE_NEO;
		} else if (totalSteps > neoMaxSteps && totalSteps <= plusMaxSteps) {
			return ProductKey.FUNCTIONAL_SCHIENE_PLUS;
		}
		return ProductKey.FUNCTIONAL_SCHIENE_MAX;
	}

	public static getCurrentStepRange( packages: FunctionALTreatmentPackages ) : string {
		if (!packages){
			return null;
		}
		if(packages.closed.length === 0 && !packages.active){
			return "0";
		}
		let fromStep: number = 0;
		let toStep: number = 0;
		packages.closed.forEach((pkg: FunctionALPackage) => {
			fromStep += pkg.steps;
			toStep += pkg.steps;
		});
		fromStep += 1;
		toStep += packages.active?.steps ?? 0;

		return `${fromStep}-${toStep}`;
	}

	public static hasPackageOfType( packages: FunctionALTreatmentPackages, packageType: FunctionALPackageType ): boolean {
		if (!packages){
			return false;
		}
		const allPackages = this.getAllPackagesAsArray(packages);
		const packagesOfType = allPackages.filter(pkg => pkg.type === packageType);
		const steps = packagesOfType.reduce((acc, pkg) => acc + pkg.steps, 0);

		return packagesOfType.length > 0 && steps > 0;
	}

	/**
	 * Checks if the refinement package has been consumed for NEO and PLUS treatments
	 * Note: this assumes, that only one refinement package is defined
	 * This function can not be used for MAX treatments
	 */
	public static hasRefinementPackageConsumed( packages: FunctionALTreatmentPackages ): boolean {
		if (!packages) {
			return false;
		}
		const activeAndClosedPackages = [...packages.closed, ...(packages.active ? [packages.active] : [])];
		return activeAndClosedPackages.length > 0 && activeAndClosedPackages.some((pkg) => pkg.type === FunctionALPackageType.REFINEMENT);
	}

	private static getAllPackagesAsArray( packages: FunctionALTreatmentPackages ) : FunctionALPackage[] {
		if(!packages){
			return [];
		}
		return [...packages.closed, ...(packages.active ? [packages.active] : []), ...(packages.pending ?? [])];
	}

	public static hasPreAlignerSalesOrder( quotation: QuotationModel ): boolean {
		return !!quotation?.salesOrders && quotation.salesOrders?.some(
			(order) => order.context?.product == ProductKey.PRE_ALIGNER
		);
	}

	public static getTotalPackageCount( packages: FunctionALTreatmentPackages ): number {
		return this.getAllPackagesAsArray(packages).length;
	}

	public static getCurrentPackageNumber( packages: FunctionALTreatmentPackages ): number {
		if(!packages){
			return 0;
		}
		return [...packages.closed, ...(packages.active ? [packages.active] : [])].length;
	}

	public static determineChargeableRefinementPackagePrice(steps: number): number {
		if (steps <= 5) {
			return FUNCTIONAL_REFINEMENT_SMALL_PACKAGE_PRICE;
		} else {
			return FUNCTIONAL_REFINEMENT_LARGE_PACKAGE_PRICE;
		}
	}
}
